/**
 * Belegliste je Belegart.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  DOCUMENT_STATUS_LABELS,
  formatAmount,
  formatDate,
  getDocumentType,
  isOverdue,
  type DocumentStatus,
  type DocumentType,
} from '@erp/shared';

import { api, withQuery } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyState, LoadingBlock, TextInput } from '../components/ui';

export type DocumentListItem = {
  id: string;
  type: DocumentType;
  number: string | null;
  lifecycle: 'draft' | 'posted' | 'cancelled';
  status: DocumentStatus;
  documentDate: string;
  dueDate: string | null;
  netTotal: number;
  grossTotal: number;
  paidAmount: number;
  subject: string | null;
  externalReference: string | null;
  isFrameworkContract: boolean;
  validUntil: string | null;
  /** Wertgewichteter Abruf-Fortschritt (Menge x Einzelpreis). */
  orderedValue: number;
  calledOffValue: number;
  partnerId: string;
  partnerName: string;
  partnerNumber: string;
  cancelledByDocumentId: string | null;
  /** Lieferschein: gesetzt, sobald eine Versandrückmeldung vorliegt. */
  shippedAt?: string | null;
  cancelsDocumentId: string | null;
};

type ListResult = {
  items: DocumentListItem[];
  total: number;
  sums: { net: number; gross: number; open: number };
};

/** Pfad-Segment je Belegart. */
export const DOCUMENT_ROUTES: Record<DocumentType, string> = {
  quote: '/verkauf/angebote',
  sales_order: '/verkauf/auftraege',
  delivery_note: '/verkauf/lieferscheine',
  invoice: '/verkauf/rechnungen',
  credit_note: '/verkauf/gutschriften',
  purchase_order: '/einkauf/bestellungen',
  goods_receipt: '/einkauf/wareneingang',
  purchase_invoice: '/einkauf/eingangsrechnungen',
};

/** Rahmenvertraege sind Auftraege mit Kennzeichen, leben aber unter eigener Adresse. */
export const FRAMEWORK_ROUTE = '/verkauf/rahmenvertraege';

/** Abruf-Fortschritt in Prozent, wertgewichtet; null ohne bewertete Positionen. */
export function callOffPercent(document: { orderedValue: number; calledOffValue: number }): number | null {
  if (!document.orderedValue) return null;
  return Math.min(100, Math.round((document.calledOffValue / document.orderedValue) * 100));
}

export function DocumentsPage({ type, framework = false }: { type: DocumentType; framework?: boolean }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const meta = getDocumentType(type);
  // Auftraege und Rahmenvertraege teilen sich den Belegtyp, nicht die Liste.
  const label = framework ? 'Rahmenvertrag' : meta.label;
  const labelPlural = framework ? 'Rahmenverträge' : meta.labelPlural;
  const frameworkFilter = framework ? 'true' : type === 'sales_order' ? 'false' : undefined;

  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState<'all' | 'draft' | 'posted' | 'cancelled'>('all');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const basePath = framework ? FRAMEWORK_ROUTE : DOCUMENT_ROUTES[type];
  const mayWrite = can(meta.side === 'sales' ? 'sales:write' : 'purchase:write');

  const { data, isLoading } = useQuery({
    queryKey: ['documents', type, frameworkFilter, search, lifecycle, offset],
    queryFn: () =>
      api.get<ListResult>(
        withQuery('/api/documents', {
          type,
          framework: frameworkFilter,
          q: search || undefined,
          lifecycle,
          limit,
          offset,
          sort: 'documentDate',
          dir: 'desc',
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const showPayment = type === 'invoice' || type === 'purchase_invoice';

  return (
    <>
      <PageHeader
        title={labelPlural}
        subtitle={
          framework
            ? 'Vereinbarte Mengen und Preise für einen Zeitraum; jeder Abruf ist ein Lieferschein aus dem Vertrag'
            : data
              ? `${data.total} Belege`
              : undefined
        }
        actions={
          mayWrite ? (
            <Link className="btn btn-primary" to={`${basePath}/neu`}>
              {label} erfassen
            </Link>
          ) : null
        }
      />

      {data && data.sums.gross !== 0 ? (
        <div className="tile-grid" style={{ marginBottom: 16 }}>
          <div className="tile">
            <div className="tile-label">Summe netto (festgeschrieben)</div>
            <div className="tile-value">{formatAmount(data.sums.net)} €</div>
          </div>
          <div className="tile">
            <div className="tile-label">Summe brutto</div>
            <div className="tile-value">{formatAmount(data.sums.gross)} €</div>
          </div>
          {showPayment ? (
            <div className="tile">
              <div className="tile-label">Davon offen</div>
              <div className="tile-value" style={data.sums.open > 0 ? { color: 'var(--warning)' } : undefined}>
                {formatAmount(data.sums.open)} €
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="card">
        <div className="table-toolbar">
          <TextInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setOffset(0);
            }}
            placeholder="Suche nach Belegnummer, Betreff oder Bestellnummer …"
          />
          <select
            className="select input"
            style={{ width: 190 }}
            value={lifecycle}
            onChange={(event) => {
              setLifecycle(event.target.value as typeof lifecycle);
              setOffset(0);
            }}
          >
            <option value="all">Alle Belege</option>
            <option value="draft">Nur Entwürfe</option>
            <option value="posted">Nur festgeschriebene</option>
            <option value="cancelled">Nur stornierte</option>
          </select>
        </div>

        {isLoading ? <LoadingBlock /> : null}

        {data && !isLoading ? (
          data.items.length === 0 ? (
            <EmptyState
              title={search ? 'Keine Treffer' : `Noch keine ${labelPlural}`}
              hint={
                search
                  ? undefined
                  : mayWrite
                    ? `Legen Sie den ersten Beleg über „${label} erfassen“ an.`
                    : undefined
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Nummer</th>
                    <th className="shrink">Datum</th>
                    <th>Partner</th>
                    <th>Betreff</th>
                    {framework ? <th className="shrink">Gültig bis</th> : null}
                    {framework ? <th className="shrink">Abgerufen</th> : null}
                    {showPayment ? <th className="shrink">Fällig</th> : null}
                    <th className="numeric">Netto</th>
                    <th className="numeric">Brutto</th>
                    <th className="shrink">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((document) => {
                    const overdue =
                      showPayment &&
                      document.lifecycle === 'posted' &&
                      document.dueDate !== null &&
                      document.paidAmount < document.grossTotal &&
                      isOverdue(document.dueDate);

                    return (
                      <tr
                        key={document.id}
                        className="clickable"
                        onClick={() => navigate(`${basePath}/${document.id}`)}
                      >
                        <td className="mono">
                          {document.number ?? <span className="faint">Entwurf</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(document.documentDate)}</td>
                        <td>
                          <div>{document.partnerName}</div>
                          <div className="faint mono">{document.partnerNumber}</div>
                        </td>
                        <td className="muted">
                          {document.isFrameworkContract && !framework ? (
                            <>
                              <Badge kind="accent">Rahmenvertrag</Badge>{' '}
                            </>
                          ) : null}
                          {document.subject ?? ''}
                        </td>
                        {framework ? (
                          <td
                            style={
                              document.validUntil && document.lifecycle === 'posted' && isOverdue(document.validUntil)
                                ? { color: 'var(--danger)', whiteSpace: 'nowrap' }
                                : { whiteSpace: 'nowrap' }
                            }
                          >
                            {document.validUntil ? formatDate(document.validUntil) : <span className="faint">offen</span>}
                          </td>
                        ) : null}
                        {framework ? (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <CallOffProgress document={document} />
                          </td>
                        ) : null}
                        {showPayment ? (
                          <td
                            style={
                              overdue
                                ? { color: 'var(--danger)', fontWeight: 600, whiteSpace: 'nowrap' }
                                : { whiteSpace: 'nowrap' }
                            }
                          >
                            {formatDate(document.dueDate)}
                          </td>
                        ) : null}
                        <td className="numeric">{formatAmount(document.netTotal)}</td>
                        <td className="numeric" style={{ fontWeight: 500 }}>
                          {formatAmount(document.grossTotal)}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 4 }}>
                            <StatusBadge document={document} overdue={overdue} />
                            {document.shippedAt && document.lifecycle === 'posted' ? (
                              <span title={`Versandt am ${new Date(document.shippedAt).toLocaleDateString('de-DE')}`}>
                                <Badge kind="success">Versandt</Badge>
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {data && data.total > limit ? (
          <div className="table-footer">
            <span>
              {offset + 1} – {Math.min(offset + limit, data.total)} von {data.total}
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset + limit >= data.total}
                onClick={() => setOffset(offset + limit)}
              >
                Weiter
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Fortschrittsbalken fuer den Abruf eines Rahmenvertrags. */
function CallOffProgress({ document }: { document: { orderedValue: number; calledOffValue: number; lifecycle: string } }) {
  const percent = callOffPercent(document);
  if (percent === null || document.lifecycle === 'draft') return <span className="faint">—</span>;
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }} title="Wertanteil der bereits gelieferten Mengen">
      <div
        style={{
          width: 72,
          height: 6,
          borderRadius: 3,
          background: 'var(--bg-sunken)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: percent >= 100 ? 'var(--success)' : 'var(--accent)',
          }}
        />
      </div>
      <span className="mono" style={{ fontSize: 12.5 }}>
        {percent} %
      </span>
    </div>
  );
}

export function StatusBadge({
  document,
  overdue,
}: {
  document: Pick<DocumentListItem, 'lifecycle' | 'status' | 'cancelsDocumentId'>;
  overdue?: boolean;
}) {
  if (document.lifecycle === 'draft') return <Badge>Entwurf</Badge>;
  if (document.lifecycle === 'cancelled') return <Badge kind="danger">Storniert</Badge>;
  if (document.cancelsDocumentId) return <Badge kind="warning">Storno</Badge>;
  if (overdue) return <Badge kind="danger">Überfällig</Badge>;

  const label = DOCUMENT_STATUS_LABELS[document.status] ?? document.status;
  const kind =
    document.status === 'paid' || document.status === 'delivered' || document.status === 'completed'
      ? 'success'
      : document.status === 'rejected' || document.status === 'expired'
        ? 'warning'
        : 'accent';

  return <Badge kind={kind}>{label}</Badge>;
}
